import React, { useState, useEffect } from 'react';
import { getLeaderboardApi } from '../../http/http';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { getCookie } from '../../utils/helper';
import SkeletonLoader from './SkeletonLoader';
import StatsBanner from './StatsBanner';
import Podium from './Podium';
import LeaderboardRow from './LeaderboardRow';

const Leaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchLeaderboard = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError(null);
    try {
      const ani = localStorage.getItem("msisdn") || getCookie("msisdn");
      const response = await getLeaderboardApi(ani);
      if (response.data && response.data.status === "SUCCESS") {
        setData(response.data);
      } else {
        throw new Error("Failed to fetch leaderboard data");
      }
    } catch (err) {
      console.error(err);
      setError("Unable to load leaderboard right now.");
    } finally {
      if (!isBackground) setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();

    const interval = setInterval(() => {
      setIsRefreshing(true);
      fetchLeaderboard(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loggedInAni = localStorage.getItem("msisdn") || getCookie("msisdn");
  
  // Calculate user rank based on the topUsers array
  const getUserRank = () => {
    if (!data?.topUsers) return '--';
    const idx = data.topUsers.findIndex(u => String(u.ani) === String(loggedInAni));
    return idx !== -1 ? idx + 1 : '--';
  };

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto p-8 min-h-[500px]">
        <SkeletonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-7xl mx-auto p-8 flex flex-col items-center justify-center min-h-[400px]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-900/20 border border-red-500/50 rounded-3xl p-10 flex flex-col items-center text-center max-w-2xl"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mb-6" />
          <h3 className="text-4xl font-bold text-white mb-4">Connection Error</h3>
          <p className="text-xl text-gray-400 mb-8">{error}</p>
          <button 
            onClick={() => fetchLeaderboard()}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full text-2xl font-semibold transition-colors flex items-center gap-3"
          >
            <RefreshCw className="w-6 h-6" /> Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  const topUsers = data?.topUsers || [];
  const topPoints = topUsers.length > 0 ? topUsers[0].totalPoints : 0;
  const userPoints = data?.userTotalPoints || 0;

  return (
    <div className="w-full max-w-[120rem] mx-auto p-6 md:p-10 pb-32 relative">
      {/* Background ambient light */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[10%] left-[10%] w-[40rem] h-[40rem] bg-purple-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute top-[30%] right-[10%] w-[50rem] h-[50rem] bg-blue-600/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="flex justify-between items-end mb-10">
        <div>
          <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-gray-400 tracking-tight">
            XR LEGENDS
          </h1>
          <p className="text-gray-400 mt-4 text-xl md:text-2xl">The elite players of YoGamezXR</p>
        </div>
        
        <AnimatePresence>
          {isRefreshing && (
            <motion.div 
              initial={{ opacity: 0, rotate: -180 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="text-purple-400 hidden md:flex items-center gap-3 bg-purple-500/10 px-5 py-2.5 rounded-full border border-purple-500/20"
            >
              <RefreshCw className="w-6 h-6 animate-spin" />
              <span className="text-base font-semibold">Live Updating</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <StatsBanner 
        userPoints={userPoints} 
        userRank={getUserRank()} 
        topPoints={topPoints} 
      />

      {topUsers.length >= 3 && <Podium topUsers={topUsers} />}

      <div className="mt-12 bg-gray-900/60 backdrop-blur-xl border border-gray-700/50 rounded-[3rem] p-6 md:p-10 shadow-2xl relative z-10">
        <div className="flex justify-between text-base md:text-lg font-bold text-gray-500 uppercase tracking-widest px-6 md:px-8 pb-6 border-b border-gray-800 mb-6">
          <span>Rank & Player</span>
          <span>Score</span>
        </div>
        
        {topUsers.length > 0 ? (
          <div className="space-y-4">
            {topUsers.slice(0, 10).map((user, index) => (
              <LeaderboardRow 
                key={`${user.ani}-${index}`}
                user={user}
                index={index}
                isCurrentUser={String(user.ani) === String(loggedInAni)}
              />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center text-gray-500 text-2xl">
            <p>No players on the leaderboard yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
